import React from 'react';

const NowContext = React.createContext(new Date());

/**
 * A Context provider that will expose a current timestamp that's safe for
 * server-side rendered components.
 *
 * There are parts of this application that want to render things differently
 * based on the current date and time. For example, there's a cutoff for when we
 * can start marketing plans for the coming plan year, but that data for the
 * coming plan year is often deployed ahead of when it can be visible. If the
 * site is deployed before that cutoff, there could be hydration errors after
 * that cutoff (the client's sense of what plan information can be shown would
 * be different from the server-side rendered content).
 *
 * We can work around this by using the build time for the initial render. After
 * the render completes, we can update the provided timestamp to the real "now."
 */
export const NowProvider: React.FC<{
  children: React.ReactNode;
  value: Date;
}> = props => {
  const [now, setNow] = React.useState(props.value);

  React.useEffect(() => {
    setNow(new Date());
  }, []);

  return (
    <NowContext.Provider value={now}>{props.children}</NowContext.Provider>
  );
};

export const useNow = (): Date => React.useContext(NowContext);
